import { useState, useMemo, useCallback } from 'react'
import { Search, Filter, ChevronDown, ChevronUp } from 'lucide-react'
import { ReviewTreeItem } from './review-tree-item'
import type { ActionNodeWithMeasures, ReviewTreeNode, MeasureFilter } from '../types'

type ReviewActionTreeProps = {
  actionTree: ActionNodeWithMeasures[]
}

// Convert API action tree to internal tree structure
function buildTreeNodes(
  nodes: ActionNodeWithMeasures[],
  depth: number = 0,
  expandAll: boolean = true
): ReviewTreeNode[] {
  return nodes.map(node => {
    const children = node.categories
      ? buildTreeNodes(node.categories, depth + 1, expandAll)
      : []

    return {
      id: node.id,
      text: node.text,
      measureReference: node.measureReference,
      measureIds: node.measureIds,
      actionSentence: node.actionSentence,
      planSentence: node.planSentence,
      isInfoSearch: node.isInfoSearch,
      isExpanded: expandAll,
      isLeafNode: children.length === 0,
      children,
      depth,
    }
  })
}

// Search within tree and get matching node IDs
function searchTree(nodes: ReviewTreeNode[], query: string): Set<number | string> {
  const matches = new Set<number | string>()
  const lowerQuery = query.toLowerCase()

  function traverse(node: ReviewTreeNode, ancestorIds: (number | string)[]) {
    const text = node.text || node.actionSentence || node.planSentence || ''
    const isMatch = text.toLowerCase().includes(lowerQuery)

    if (isMatch) {
      matches.add(node.id)
      // Also add all ancestors so they stay expanded
      ancestorIds.forEach(id => matches.add(id))
    }

    node.children.forEach(child => {
      traverse(child, [...ancestorIds, node.id])
    })
  }

  nodes.forEach(node => traverse(node, []))
  return matches
}

// Filter tree by measure status
function filterByMeasures(
  nodes: ReviewTreeNode[],
  filter: MeasureFilter
): ReviewTreeNode[] {
  if (filter === 'all') return nodes

  function shouldInclude(node: ReviewTreeNode): boolean {
    const hasMeasures = node.measureReference || (node.measureIds && node.measureIds.length > 0)

    if (filter === 'with-measures') return !!hasMeasures
    if (filter === 'without-measures') return !hasMeasures
    return true
  }

  function filterNode(node: ReviewTreeNode): ReviewTreeNode | null {
    const filteredChildren = node.children
      .map(filterNode)
      .filter((n): n is ReviewTreeNode => n !== null)

    // Include node if it matches filter OR has matching descendants
    if (shouldInclude(node) || filteredChildren.length > 0) {
      return {
        ...node,
        children: filteredChildren,
        isLeafNode: filteredChildren.length === 0,
      }
    }

    return null
  }

  return nodes
    .map(filterNode)
    .filter((n): n is ReviewTreeNode => n !== null)
}

export function ReviewActionTree({ actionTree }: ReviewActionTreeProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [measureFilter, setMeasureFilter] = useState<MeasureFilter>('all')
  const [expandedIds, setExpandedIds] = useState<Set<number | string>>(new Set())
  const [allExpanded, setAllExpanded] = useState(true)

  // Build tree structure from action data
  const treeNodes = useMemo(() => {
    return buildTreeNodes(actionTree, 0, true)
  }, [actionTree])

  // Get all node IDs for expand/collapse all
  const allNodeIds = useMemo(() => {
    const ids = new Set<number | string>()
    function collect(nodes: ReviewTreeNode[]) {
      nodes.forEach(node => {
        ids.add(node.id)
        collect(node.children)
      })
    }
    collect(treeNodes)
    return ids
  }, [treeNodes])

  // Initialize expanded state to all
  useMemo(() => {
    if (expandedIds.size === 0 && allNodeIds.size > 0) {
      setExpandedIds(new Set(allNodeIds))
    }
  }, [allNodeIds, expandedIds.size])

  // Apply filters
  const filteredNodes = useMemo(() => {
    let nodes = treeNodes

    // Apply measure filter
    nodes = filterByMeasures(nodes, measureFilter)

    return nodes
  }, [treeNodes, measureFilter])

  // Search matches
  const searchMatches = useMemo(() => {
    if (searchQuery.length < 2) return null
    return searchTree(filteredNodes, searchQuery)
  }, [filteredNodes, searchQuery])

  // Update tree nodes with expansion state
  const displayNodes = useMemo(() => {
    function updateExpansion(nodes: ReviewTreeNode[]): ReviewTreeNode[] {
      return nodes.map(node => ({
        ...node,
        isExpanded: expandedIds.has(node.id),
        children: updateExpansion(node.children),
      }))
    }
    return updateExpansion(filteredNodes)
  }, [filteredNodes, expandedIds])

  const handleToggle = useCallback((nodeId: number | string) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(nodeId)) {
        next.delete(nodeId)
      } else {
        next.add(nodeId)
      }
      return next
    })
  }, [])

  const handleExpandAll = useCallback(() => {
    setExpandedIds(new Set(allNodeIds))
    setAllExpanded(true)
  }, [allNodeIds])

  const handleCollapseAll = useCallback(() => {
    setExpandedIds(new Set())
    setAllExpanded(false)
  }, [])

  return (
    <div className="flex flex-col h-full">
      {/* Search and filter controls */}
      <div className="flex items-center gap-3 p-4 border-b border-gray-200 bg-white sticky top-0 z-10">
        {/* Search input */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search actions..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              &times;
            </button>
          )}
        </div>

        {/* Measure filter */}
        <div className="relative">
          <select
            value={measureFilter}
            onChange={e => setMeasureFilter(e.target.value as MeasureFilter)}
            className="appearance-none pl-8 pr-8 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All actions</option>
            <option value="with-measures">With measures</option>
            <option value="without-measures">Missing measures</option>
          </select>
          <Filter className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        </div>

        {/* Expand/collapse all */}
        <button
          onClick={allExpanded ? handleCollapseAll : handleExpandAll}
          className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md"
        >
          {allExpanded ? (
            <>
              <ChevronUp className="w-4 h-4" />
              Collapse
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4" />
              Expand
            </>
          )}
        </button>
      </div>

      {/* Search results info */}
      {searchMatches && (
        <div className="px-4 py-2 bg-yellow-50 text-sm text-yellow-700 border-b border-yellow-200">
          Found {searchMatches.size} matching actions
        </div>
      )}

      {/* Tree content */}
      <div className="flex-1 overflow-auto p-4">
        {displayNodes.length > 0 ? (
          displayNodes.map(node => (
            <ReviewTreeItem
              key={node.id}
              node={node}
              onToggle={handleToggle}
              searchQuery={searchQuery}
              highlightMissing={measureFilter === 'without-measures'}
            />
          ))
        ) : (
          <div className="text-center py-8 text-gray-500">
            {measureFilter !== 'all'
              ? 'No actions match the current filter'
              : 'No actions in this simulation'}
          </div>
        )}
      </div>
    </div>
  )
}
